import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../api/query-keys'
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  selectTriggerVariants,
} from '@speaking-coach/ui'
import { Listbox, ListboxMessage, ListboxOption, ListboxSearch, useListboxId } from '../components/ui/listbox'
import { sessionsClient, type OptionKind } from './api'

export function SessionPicker({ kind, label, value, onChange }: { kind: OptionKind; label: string; value: string; onChange: (value: string) => void }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [active, setActive] = useState('')
  const listId = useListboxId()
  useEffect(() => { const timer = window.setTimeout(() => setSearch(query.trim()), 250); return () => window.clearTimeout(timer) }, [query])
  const params = { q: search }
  const results = useQuery({ queryKey: queryKeys.sessionOptions(kind, params), queryFn: () => sessionsClient.options(kind, params), enabled: open })
  const selected = useQuery({ queryKey: queryKeys.sessionOptions(kind, { id: value }), queryFn: () => sessionsClient.options(kind, { id: value }), enabled: Boolean(value) })
  const rows = results.data?.items ?? []
  const choose = (id: string) => { onChange(id === '__all__' ? '' : id); setOpen(false) }
  return <Popover open={open} onOpenChange={setOpen}>
    <div className="relative flex w-full items-center">
      <PopoverTrigger id={`session-${kind}`} role="combobox" aria-label={label} onKeyDown={event => { if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); setOpen(true) } }} className={`${selectTriggerVariants({ size: 'sm' })} ${value ? 'pr-14' : 'pr-9'}`}>
        <span className="truncate">{value ? selected.data?.items[0]?.label ?? (selected.isPending ? 'Loading…' : `${label} unavailable`) : `All ${kind}`}</span>
      </PopoverTrigger>
      {value && <Button variant="ghost" size="iconSm" className="absolute right-7" aria-label={`Clear ${label.toLowerCase()} filter`} onClick={() => choose('__all__')}><Icon name="clear" size={14} /></Button>}
      <Icon name="choose" className="pointer-events-none absolute right-3 opacity-60" />
    </div>
    <PopoverContent className="w-(--anchor-width) p-0">
      <ListboxSearch listId={listId} label={`Search ${label.toLowerCase()}`} placeholder={kind === 'students' ? 'Search name or email…' : 'Search name…'} value={query} onValueChange={setQuery} activeId={active} optionIds={[...rows.map(row => row.id), '__all__']} onActiveIdChange={setActive} onSelect={choose} onDismiss={() => setOpen(false)} />
      <Listbox id={listId} label={label}>
        {results.isPending && <ListboxMessage>Searching…</ListboxMessage>}
        {results.isError && <li role="presentation" className="p-3"><p role="alert">Could not load options.</p><Button size="sm" variant="secondary" onClick={() => void results.refetch()}>Try again</Button></li>}
        {results.isSuccess && rows.length === 0 && <ListboxMessage>No matches.</ListboxMessage>}
        {rows.map(row => <ListboxOption key={row.id} listId={listId} value={row.id} active={active === row.id} onHover={() => setActive(row.id)} onSelect={() => choose(row.id)}><Icon name="selected" className={`shrink-0 ${value === row.id ? '' : 'opacity-0'}`} /><span className="min-w-0 break-words">{row.label}<span className="block text-caption text-text-secondary">{row.context}</span></span></ListboxOption>)}
        {results.data?.hasMore && <ListboxMessage>Keep typing to narrow the results.</ListboxMessage>}
        <ListboxOption listId={listId} value="__all__" active={active === '__all__'} onHover={() => setActive('__all__')} onSelect={() => choose('__all__')}>All {kind}</ListboxOption>
      </Listbox>
    </PopoverContent>
  </Popover>
}
